import { useTranslationUserProfiles } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	normalizePhoneNumberForBE,
	normalizePhoneNumberForFormValueComparison,
} from '@dealstack/shared/lib/format/string';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { isSameDay } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { userNameUpdated } from '@dealstack/frontend/shared/mechanism/redux/reducers/user/user';
import * as Yup from 'yup';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getUserPersons,
	getUserProfile,
	updateUserPersonalDetails,
} from '@dealstack/frontend/shared/dataExchange/services/user.service';
import { UpdateUserPersonalDetailsDto } from '@dealstack/shared/lib/generated';
import { getMainAddress } from '@dealstack/frontend/shared/address.ts';
import { useSavedSignature } from '@dealstack/frontend/shared/components/Signature/hooks/useSavedSignature';
import { useDeleteUserSignature } from '@dealstack/frontend/shared/components/Signature/hooks/useDeleteUserSignature';

export enum PERSONAL_INFO_FORM {
	COUNTRY = 'country',
	CITY = 'city',
	POST_CODE = 'postCode',
	STREET = 'street',
	HOUSE_NUMBER = 'houseNumber',
	TELEPHONE_PREFIX = 'telephonePrefix',
	PHONE_NUMBER = 'phoneNumber',
	LAST_NAME = 'lastName',
	FIRST_NAME = 'firstName',
	BIRTH_DATE = 'birthDate',
}
export const useManagePersonalDetailsController = () => {
	const tUserProfiles = useTranslationUserProfiles();
	const dispatch = useDispatch();
	const getUserProfilesRequest = useQuery({
		queryKey: [API_PATHS.USER_PROFILE_GET],
		queryFn: ({ signal }) => getUserProfile(API_PATHS.USER_PROFILE_GET, signal),
	});
	const getUserPersonsRequest = useQuery({
		queryKey: ['user-persons'],
		queryFn: () => getUserPersons(),
	});

	const updatePersonalDetailsRequest = useMutation({
		mutationFn: (variables: Parameters<typeof updateUserPersonalDetails>[0]) =>
			updateUserPersonalDetails(variables),
	});
	const commonYup = useCommonYupValidation();

	const [isEditModeEnabled, setIsEditModeEnabled] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [showSignatureModal, setShowSignatureModal] = useState(false);

	const savedSignature = useSavedSignature();
	const deleteSignature = useDeleteUserSignature();

	const validationSchema = useMemo(() => {
		return Yup.object().shape({
			[PERSONAL_INFO_FORM.COUNTRY]: commonYup.country.required,
			[PERSONAL_INFO_FORM.CITY]: commonYup.string.required,
			[PERSONAL_INFO_FORM.STREET]: commonYup.string.required,
			[PERSONAL_INFO_FORM.HOUSE_NUMBER]: commonYup.string.required,
			[PERSONAL_INFO_FORM.POST_CODE]: commonYup.string.required,
			[PERSONAL_INFO_FORM.PHONE_NUMBER]: commonYup.phone.required,
			[PERSONAL_INFO_FORM.LAST_NAME]: commonYup.string.required,
			[PERSONAL_INFO_FORM.FIRST_NAME]: commonYup.string.required,
			[PERSONAL_INFO_FORM.BIRTH_DATE]: commonYup.date.past.required,
		});
	}, []);

	const user = getUserProfilesRequest?.data?.payload;
	const firstPersonData = getUserPersonsRequest.data?.persons?.[0];

	const defaultValues = useMemo(() => {
		const personMainAddress = getMainAddress(firstPersonData?.addresses);
		return {
			[PERSONAL_INFO_FORM.COUNTRY]: personMainAddress?.country ?? '',
			[PERSONAL_INFO_FORM.CITY]: personMainAddress?.city ?? '',
			[PERSONAL_INFO_FORM.STREET]: personMainAddress?.street ?? '',
			[PERSONAL_INFO_FORM.HOUSE_NUMBER]: personMainAddress?.houseNumber ?? '',
			[PERSONAL_INFO_FORM.POST_CODE]: personMainAddress?.postCode ?? '',
			[PERSONAL_INFO_FORM.TELEPHONE_PREFIX]: firstPersonData?.telephonePrefix ?? '',
			[PERSONAL_INFO_FORM.PHONE_NUMBER]: firstPersonData?.phoneNumber
				? firstPersonData?.telephonePrefix + firstPersonData?.phoneNumber
				: '',
			[PERSONAL_INFO_FORM.BIRTH_DATE]: firstPersonData?.dateOfBirth ?? '',
			[PERSONAL_INFO_FORM.FIRST_NAME]: firstPersonData?.firstName ?? '',
			[PERSONAL_INFO_FORM.LAST_NAME]: firstPersonData?.lastName ?? '',
		};
	}, [firstPersonData]);

	const personalDetailsForm = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});

	const changeToViewMode = () => {
		personalDetailsForm.reset(defaultValues);
		setIsEditModeEnabled(false);
		setShowModal(false);
	};

	useEffect(() => {
		if (!user || !firstPersonData) {
			return;
		}
		personalDetailsForm.reset(defaultValues);
	}, [user, firstPersonData]);

	const onSubmit = async (formData: UpdateUserPersonalDetailsDto) => {
		const phoneNumber = normalizePhoneNumberForBE(
			formData[PERSONAL_INFO_FORM.PHONE_NUMBER],
			formData[PERSONAL_INFO_FORM.TELEPHONE_PREFIX]
		);
		const birthDate = formData[PERSONAL_INFO_FORM.BIRTH_DATE] || '';

		updatePersonalDetailsRequest.mutate(
			{
				...formData,
				phoneNumber,
				birthDate,
				addressId: user?.address?.id,
			},
			{
				onSuccess: () => {
					const isNameChanged =
						personalDetailsForm.formState.dirtyFields[PERSONAL_INFO_FORM.FIRST_NAME] ||
						personalDetailsForm.formState.dirtyFields[PERSONAL_INFO_FORM.LAST_NAME];

					showToastV2({
						title: tUserProfiles('toast.title', {
							section_name: 'personal details',
						}),
						message: tUserProfiles('toast.description'),
					});
					void getUserProfilesRequest.refetch();
					void getUserPersonsRequest.refetch();
					setIsEditModeEnabled(false);
					if (isNameChanged) {
						dispatch(
							userNameUpdated({
								firstName: formData[PERSONAL_INFO_FORM.FIRST_NAME],
								lastName: formData[PERSONAL_INFO_FORM.LAST_NAME],
							})
						);
					}
				},
			}
		);
	};

	const isDirty = useMemo(() => {
		const formDateValue = personalDetailsForm.getValues(PERSONAL_INFO_FORM.BIRTH_DATE);
		const isDateChanged =
			formDateValue && !isSameDay(new Date(defaultValues.birthDate), new Date(formDateValue));
		const phoneNumber = normalizePhoneNumberForFormValueComparison(
			personalDetailsForm.getValues(PERSONAL_INFO_FORM.PHONE_NUMBER)
		);

		const isPhoneNumberChange = phoneNumber !== defaultValues.phoneNumber;

		const isOtherFieldChanged =
			personalDetailsForm.formState.dirtyFields[PERSONAL_INFO_FORM.FIRST_NAME] ||
			personalDetailsForm.formState.dirtyFields[PERSONAL_INFO_FORM.LAST_NAME] ||
			personalDetailsForm.formState.dirtyFields[PERSONAL_INFO_FORM.HOUSE_NUMBER] ||
			personalDetailsForm.formState.dirtyFields[PERSONAL_INFO_FORM.STREET] ||
			personalDetailsForm.formState.dirtyFields[PERSONAL_INFO_FORM.CITY] ||
			personalDetailsForm.formState.dirtyFields[PERSONAL_INFO_FORM.POST_CODE] ||
			personalDetailsForm.formState.dirtyFields[PERSONAL_INFO_FORM.COUNTRY];
		return isDateChanged || isPhoneNumberChange || isOtherFieldChanged;
	}, [defaultValues, personalDetailsForm.watch(), personalDetailsForm.formState]);

	return {
		onSubmit,
		isEditModeEnabled,
		setIsEditModeEnabled,
		personalDetailsForm,
		showModal,
		setShowModal,
		isLoading: getUserProfilesRequest?.isLoading,
		changeToViewMode,
		user,
		firstPerson: firstPersonData,
		isDirty,
		showSignatureModal,
		setShowSignatureModal,
		savedSignature,
		deleteSignature,
	};
};
